import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useApi, useArchivo, useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '@plataforma/web/formato';
import { usePuede } from '../permisos';
import { AnticipoEncargo } from '../componentes/AnticipoEncargo';

/** Compra por encargo (arquitectura 2.15): pedir un auto a Siniestrados, ver la propuesta y decidir. */
interface TipoCompra {
  clave: string;
  nombre: string;
  descripcion: string;
  nota: string | null;
}
interface Captura {
  id: string;
  nombre: string;
  tipoContenido: string;
}
interface Anticipo {
  pujaMaximaUsd: string;
  cargosUsd: string;
  trasladoUsd: string;
  tipoCambio: string;
  trasladoMxn: string;
  importacionMxn: string;
  comisionMxn: string;
  gastosMxn: string;
  totalMxn: string;
}
interface Propuesta {
  dictamen: 'viable' | 'revisar' | 'no_viable';
  motivos: string[];
  semaforo: string;
  pujaMaximaUsd: string | null;
  costoPuestoEnLoteMxn: string | null;
  reparacionEstimadaMxn: string;
  costosVentaMxn: string;
  valorEsperadoVentaMxn: string;
  gananciaEsperadaMxn: string | null;
  comision: { porcentaje: string; minimoMxn: string };
  anticipo: Anticipo | null;
}
interface Candidato {
  id: string;
  subasta: string;
  numeroLote: string;
  urlReferencia: string | null;
  estado: string;
  motivo: string | null;
  ficha: {
    vin: string | null;
    anio: number | null;
    marca: string | null;
    modelo: string | null;
    titulo: string | null;
    danoPrimario: string | null;
    danoSecundario: string | null;
    motor: string | null;
    odometro: number | null;
    odometroReal: boolean | null;
    ubicacion: string | null;
    fechaSubasta: string | null;
    fotos: number | null;
  };
  propuesta: Propuesta | null;
  capturas: Captura[];
}
interface Encargo {
  id: string;
  folio: string;
  estado: string;
  tipoCompra: string;
  criterios: {
    tipoAuto: string;
    anioDesde: number | null;
    anioHasta: number | null;
    marcas: string[];
    danoAceptable: string[];
    tituloAceptable: string[];
    uso: string;
  };
  presupuestoMxn: string;
  notas: string | null;
  motivo: string | null;
  propuestaEnviadaEn: string | null;
  creadoEn: string;
  candidatos: Candidato[];
}

const ESTADO: Record<string, string> = {
  solicitado: 'Recibido',
  en_busqueda: 'Buscando',
  propuesta_enviada: 'Propuesta lista',
  aceptado: 'Aceptado',
  anticipo_pagado: 'Anticipo pagado',
  en_puja: 'En puja',
  ganado: 'Ganado',
  perdido: 'No se ganó',
  importando: 'Importando',
  entregado: 'Entregado',
  liquidado: 'Liquidado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
};
const CLASE_ESTADO: Record<string, string> = {
  propuesta_enviada: 'aviso',
  aceptado: 'exito',
  ganado: 'exito',
  entregado: 'exito',
  liquidado: 'exito',
};
const DICTAMEN: Record<Propuesta['dictamen'], [string, string]> = {
  viable: ['Viable', 'exito'],
  revisar: ['Revisar', 'aviso'],
  no_viable: ['No viable', 'error'],
};
const USOS: [string, string][] = [
  ['revender', 'Revenderlo'],
  ['reparar', 'Repararlo y venderlo'],
  ['desarmar', 'Desarmarlo (yonke)'],
];
const DANOS: [string, string][] = [
  ['frontal', 'Frontal'],
  ['lateral', 'Lateral'],
  ['trasero', 'Trasero'],
  ['granizo', 'Granizo'],
  ['mecanico', 'Mecánico'],
  ['inundacion', 'Inundación'],
  ['incendio', 'Incendio'],
  ['vandalismo', 'Vandalismo'],
  ['otro', 'Otro'],
];
const TITULOS: [string, string][] = [
  ['limpio', 'Limpio'],
  ['salvage', 'Salvage'],
  ['rebuilt', 'Rebuilt'],
];
const MOTOR: Record<string, string> = {
  run_and_drive: 'Arranca y avanza',
  start: 'Solo arranca',
  no_start: 'No arranca',
};
const CANCELABLES = new Set(['solicitado', 'en_busqueda', 'propuesta_enviada', 'aceptado']);
const usd = (v: string | null) =>
  v === null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const etiqueta = (lista: [string, string][], clave: string) =>
  lista.find(([v]) => v === clave)?.[1] ?? clave;

export function Encargos() {
  const lista = useConsulta<Encargo[]>('/sourcing/encargos');
  const puede = usePuede();
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Encargos</h1>
          <div className="subtitulo">
            Pídele a Siniestrados que busque y compre un auto por ti; tú decides con la puja máxima
            y el anticipo a la vista.
          </div>
        </div>
        <div className="relleno" />
        {puede('encargos.pedir') && (
          <Link className="boton primario" to="/encargos/nuevo">
            Encargar un auto
          </Link>
        )}
      </div>
      {lista.error && <p className="error">{lista.error}</p>}
      <section className="seccion">
        <table>
          <thead>
            <tr>
              <th>Folio</th>
              <th>Qué buscas</th>
              <th>Tipo de compra</th>
              <th>Presupuesto</th>
              <th>Estado</th>
              <th>Candidatos</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {(lista.datos ?? []).map((e) => (
              <tr key={e.id}>
                <td>
                  <Link to={`/encargos/${e.id}`}>{e.folio}</Link>
                </td>
                <td>{e.criterios.tipoAuto}</td>
                <td>{e.tipoCompra}</td>
                <td>{mxn(e.presupuestoMxn)}</td>
                <td>
                  <span className={`etiqueta ${CLASE_ESTADO[e.estado] ?? ''}`}>
                    {ESTADO[e.estado] ?? e.estado}
                  </span>
                </td>
                <td>{e.candidatos.filter((c) => c.estado === 'propuesto').length}</td>
                <td>{fechaCorta(e.creadoEn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {lista.datos?.length === 0 && <p className="vacio">Aún no has encargado ningún auto.</p>}
      </section>
    </>
  );
}

export function NuevoEncargo() {
  const api = useApi();
  const navegar = useNavigate();
  const tipos = useConsulta<TipoCompra[]>('/sourcing/encargos/tipos-compra');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const texto = (n: string) => String(d.get(n) ?? '').trim();
    const numero = (n: string) => (texto(n) ? Number(texto(n)) : undefined);
    setError(null);
    setEnviando(true);
    try {
      const creado = await api<{ id: string }>('POST', '/sourcing/encargos', {
        tipoCompra: texto('tipoCompra'),
        tipoAuto: texto('tipoAuto'),
        ...(numero('anioDesde') && { anioDesde: numero('anioDesde') }),
        ...(numero('anioHasta') && { anioHasta: numero('anioHasta') }),
        marcas: texto('marcas')
          .split(',')
          .map((m) => m.trim())
          .filter(Boolean),
        danoAceptable: d.getAll('dano').map(String),
        tituloAceptable: d.getAll('titulo').map(String),
        uso: texto('uso'),
        presupuestoMxn: texto('presupuestoMxn'),
        ...(texto('notas') && { notas: texto('notas') }),
      });
      navegar(`/encargos/${creado.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/encargos">Encargos</Link> › nuevo
          </div>
          <h1>Encargar un auto</h1>
        </div>
      </div>
      <form className="seccion cuerpo formulario" onSubmit={(e) => void enviar(e)}>
        <label className="campo">
          <span>Tipo de compra</span>
          <select name="tipoCompra" required defaultValue="">
            <option value="" disabled>
              Elige…
            </option>
            {(tipos.datos ?? []).map((t) => (
              <option key={t.clave} value={t.clave} title={t.descripcion}>
                {t.nombre}
              </option>
            ))}
          </select>
          <small className="subtitulo">
            Decide la importación que se estima y lo que va en el anticipo.
          </small>
        </label>
        <label className="campo">
          <span>Qué auto buscas</span>
          <input
            name="tipoAuto"
            required
            minLength={2}
            maxLength={80}
            placeholder="Pickup doble cabina, sedán compacto, SUV familiar…"
          />
        </label>
        <div className="acciones">
          <label className="campo">
            <span>Año desde</span>
            <input name="anioDesde" type="number" min={1950} max={2100} inputMode="numeric" />
          </label>
          <label className="campo">
            <span>Año hasta</span>
            <input name="anioHasta" type="number" min={1950} max={2100} inputMode="numeric" />
          </label>
        </div>
        <label className="campo">
          <span>Marcas (separadas por coma, opcional)</span>
          <input name="marcas" maxLength={200} placeholder="Toyota, Nissan, Ford" />
        </label>
        <fieldset className="campo">
          <span>Daño aceptable</span>
          <div className="acciones">
            {DANOS.map(([v, t]) => (
              <label key={v}>
                <input type="checkbox" name="dano" value={v} /> {t}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="campo">
          <span>Título aceptable</span>
          <div className="acciones">
            {TITULOS.map(([v, t]) => (
              <label key={v}>
                <input type="checkbox" name="titulo" value={v} defaultChecked={v === 'limpio'} />{' '}
                {t}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="campo">
          <span>Para qué lo quieres</span>
          <select name="uso" defaultValue="revender">
            {USOS.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span>Presupuesto puesto en lote (MXN)</span>
          <input name="presupuestoMxn" required inputMode="decimal" placeholder="250000" />
          <small className="subtitulo">
            Lo que quieres que cueste el auto ya en tu lote: compra, cargos, traslado e importación.
          </small>
        </label>
        <label className="campo">
          <span>Notas para Compras (opcional)</span>
          <textarea name="notas" maxLength={1000} rows={3} />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="acciones">
          <button className="boton primario" disabled={enviando || !tipos.datos}>
            {enviando ? 'Enviando…' : 'Encargar'}
          </button>
          <Link className="boton" to="/encargos">
            Cancelar
          </Link>
        </div>
      </form>
    </>
  );
}

function Miniatura({ captura }: { captura: Captura }) {
  const url = useArchivo(`/sourcing/encargos/capturas/${captura.id}`);
  if (captura.tipoContenido === 'application/pdf')
    return url ? (
      <a className="boton" href={url} target="_blank" rel="noreferrer">
        {captura.nombre}
      </a>
    ) : (
      <span className="subtitulo">{captura.nombre}</span>
    );
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt={captura.nombre}
        style={{ maxWidth: 160, maxHeight: 120, borderRadius: 6 }}
      />
    </a>
  ) : (
    <span className="subtitulo">Cargando {captura.nombre}…</span>
  );
}

function TarjetaCandidato({
  candidato: c,
  puedeDecidir,
  onDecidir,
}: {
  candidato: Candidato;
  puedeDecidir: boolean;
  onDecidir: (decision: 'aceptar' | 'rechazar', motivo?: string) => Promise<void>;
}) {
  const p = c.propuesta;
  const [dictamen, claseDictamen] = p ? DICTAMEN[p.dictamen] : ['Sin propuesta', ''];
  const f = c.ficha;
  const rechazar = async () => {
    const motivo = window.prompt('¿Por qué lo rechazas? (ayuda a Compras a buscar mejor)');
    if (motivo && motivo.trim().length >= 3) await onDecidir('rechazar', motivo.trim());
  };
  return (
    <article className="tarjeta">
      <div className="encabezado">
        <div>
          <strong>
            {f.anio ?? ''} {f.marca ?? ''} {f.modelo ?? ''}
          </strong>
          <div className="subtitulo">
            {c.subasta.toUpperCase()} · lote {c.numeroLote}
            {c.urlReferencia && (
              <>
                {' · '}
                <a href={c.urlReferencia} target="_blank" rel="noreferrer">
                  ver en la subasta
                </a>
              </>
            )}
          </div>
        </div>
        <div className="relleno" />
        <span className={`etiqueta ${claseDictamen}`}>{dictamen}</span>
        {c.estado !== 'propuesto' && (
          <span className="etiqueta">{ESTADO[c.estado] ?? c.estado}</span>
        )}
      </div>
      <div className="resumen">
        <div>
          <small>VIN</small>
          <span className="vin">{f.vin ?? '—'}</span>
        </div>
        <div>
          <small>Título</small>
          <span>{f.titulo ? etiqueta(TITULOS, f.titulo) : '—'}</span>
        </div>
        <div>
          <small>Daño</small>
          <span>
            {f.danoPrimario ?? '—'}
            {f.danoSecundario ? ` / ${f.danoSecundario}` : ''}
          </span>
        </div>
        <div>
          <small>Motor</small>
          <span>{f.motor ? (MOTOR[f.motor] ?? f.motor) : '—'}</span>
        </div>
        <div>
          <small>Odómetro</small>
          <span>
            {f.odometro === null ? '—' : `${f.odometro.toLocaleString('es-MX')} mi`}
            {f.odometroReal === false ? ' (no real)' : ''}
          </span>
        </div>
        <div>
          <small>Subasta</small>
          <span>
            {f.fechaSubasta ? fechaCorta(f.fechaSubasta) : '—'}
            {f.ubicacion ? ` · ${f.ubicacion}` : ''}
          </span>
        </div>
      </div>
      {c.capturas.length > 0 && (
        <div className="acciones" style={{ flexWrap: 'wrap' }}>
          {c.capturas.map((cap) => (
            <Miniatura key={cap.id} captura={cap} />
          ))}
        </div>
      )}
      {p && (
        <>
          <div className="resumen">
            <div>
              <small>Puja máxima</small>
              <span className="grande">{usd(p.pujaMaximaUsd)}</span>
            </div>
            <div>
              <small>Costo puesto en lote</small>
              <span>{p.costoPuestoEnLoteMxn ? mxn(p.costoPuestoEnLoteMxn) : '—'}</span>
            </div>
            <div>
              <small>Valor esperado de venta</small>
              <span>{mxn(p.valorEsperadoVentaMxn)}</span>
            </div>
            <div>
              <small>Ganancia esperada</small>
              <span>{p.gananciaEsperadaMxn ? mxn(p.gananciaEsperadaMxn) : '—'}</span>
            </div>
          </div>
          {p.anticipo && (
            <table>
              <caption>Anticipo que pagas antes de que Siniestrados puje</caption>
              <tbody>
                <tr>
                  <td>Puja máxima</td>
                  <td>
                    {usd(p.anticipo.pujaMaximaUsd)} × {p.anticipo.tipoCambio}
                  </td>
                </tr>
                <tr>
                  <td>Cargos de la subasta</td>
                  <td>{usd(p.anticipo.cargosUsd)}</td>
                </tr>
                <tr>
                  <td>Traslado</td>
                  <td>
                    {usd(p.anticipo.trasladoUsd)} + {mxn(p.anticipo.trasladoMxn)}
                  </td>
                </tr>
                <tr>
                  <td>Importación estimada</td>
                  <td>{mxn(p.anticipo.importacionMxn)}</td>
                </tr>
                <tr>
                  <td>Comisión de Siniestrados ({p.comision.porcentaje} %)</td>
                  <td>{mxn(p.anticipo.comisionMxn)}</td>
                </tr>
                <tr>
                  <td>Gastos administrativos</td>
                  <td>{mxn(p.anticipo.gastosMxn)}</td>
                </tr>
                <tr>
                  <th>Total del anticipo</th>
                  <th>{mxn(p.anticipo.totalMxn)}</th>
                </tr>
              </tbody>
            </table>
          )}
          {p.motivos.length > 0 && (
            <ul>
              {p.motivos.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </>
      )}
      {c.motivo && c.estado !== 'propuesto' && <p className="subtitulo">Motivo: {c.motivo}</p>}
      {puedeDecidir && c.estado === 'propuesto' && (
        <div className="acciones">
          <button className="boton primario" onClick={() => void onDecidir('aceptar')}>
            Aceptar este auto
          </button>
          <button className="boton" onClick={() => void rechazar()}>
            Rechazar
          </button>
        </div>
      )}
    </article>
  );
}

export function EncargoDetalle() {
  const { id } = useParams();
  const api = useApi();
  const puede = usePuede();
  const consulta = useConsulta<Encargo>(id ? `/sourcing/encargos/${id}` : null);
  const [error, setError] = useState<string | null>(null);
  const e = consulta.datos;
  const decidir = async (
    candidatoId: string,
    decision: 'aceptar' | 'rechazar',
    motivo?: string,
  ) => {
    setError(null);
    try {
      await api('POST', `/sourcing/encargos/${id}/candidatos/${candidatoId}/decision`, {
        decision,
        ...(motivo && { motivo }),
      });
      consulta.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const cancelar = async () => {
    const motivo = window.prompt('¿Por qué cancelas el encargo?');
    if (!motivo || motivo.trim().length < 3) return;
    setError(null);
    try {
      await api('POST', `/sourcing/encargos/${id}/cancelar`, { motivo: motivo.trim() });
      consulta.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  if (consulta.error) return <p className="error">{consulta.error}</p>;
  if (!e) return <p className="subtitulo">Cargando…</p>;
  const visibles = e.candidatos.filter((c) => c.estado !== 'descartado');
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/encargos">Encargos</Link> › {e.folio}
          </div>
          <h1>{e.criterios.tipoAuto}</h1>
          <div className="subtitulo">
            {e.tipoCompra} · para {etiqueta(USOS, e.criterios.uso).toLowerCase()} · pedido el{' '}
            {fechaCorta(e.creadoEn)}
          </div>
        </div>
        <div className="relleno" />
        <span className={`etiqueta ${CLASE_ESTADO[e.estado] ?? ''}`}>
          {ESTADO[e.estado] ?? e.estado}
        </span>
        {puede('encargos.decidir') && CANCELABLES.has(e.estado) && (
          <button className="boton" onClick={() => void cancelar()}>
            Cancelar encargo
          </button>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      <section className="seccion cuerpo">
        <div className="resumen">
          <div>
            <small>Presupuesto puesto en lote</small>
            <span className="grande">{mxn(e.presupuestoMxn)}</span>
          </div>
          <div>
            <small>Años</small>
            <span>
              {e.criterios.anioDesde ?? '…'} a {e.criterios.anioHasta ?? '…'}
            </span>
          </div>
          <div>
            <small>Marcas</small>
            <span>{e.criterios.marcas.length ? e.criterios.marcas.join(', ') : 'Cualquiera'}</span>
          </div>
          <div>
            <small>Daño aceptable</small>
            <span>
              {e.criterios.danoAceptable.length
                ? e.criterios.danoAceptable.map((d) => etiqueta(DANOS, d)).join(', ')
                : 'Cualquiera'}
            </span>
          </div>
          <div>
            <small>Título aceptable</small>
            <span>{e.criterios.tituloAceptable.map((t) => etiqueta(TITULOS, t)).join(', ')}</span>
          </div>
        </div>
        {e.notas && <p>{e.notas}</p>}
        {e.motivo && e.estado !== 'propuesta_enviada' && (
          <p className="subtitulo">Último motivo: {e.motivo}</p>
        )}
      </section>
      <section className="seccion cuerpo">
        <h2>Candidatos</h2>
        {visibles.length === 0 && (
          <p className="vacio">
            {e.estado === 'solicitado' || e.estado === 'en_busqueda'
              ? 'Compras está buscando. Te avisamos cuando haya una propuesta.'
              : 'Sin candidatos.'}
          </p>
        )}
        <div className="tarjetas">
          {visibles.map((c) => (
            <TarjetaCandidato
              key={c.id}
              candidato={c}
              puedeDecidir={puede('encargos.decidir') && e.estado === 'propuesta_enviada'}
              onDecidir={(decision, motivo) => decidir(c.id, decision, motivo)}
            />
          ))}
        </div>
      </section>
      <AnticipoEncargo encargoId={e.id} estado={e.estado} />
    </>
  );
}
